import { useMutation, ApolloCache } from "@apollo/client";
import { TOGGLE_DOCUMENT_SELECTION, BULK_TOGGLE_DOCUMENT_SELECTIONS } from "@chat/graphql/mutations/toggleDocumentSelection";

// Hilfsfunktion, um den Dokumentenstatus im Cache zu aktualisieren
const updateDocumentInCache = (
    cache: ApolloCache<any>, 
    documentId: string, 
    isActive: boolean
) => {
    const cacheId = cache.identify({ __typename: 'Document', id: documentId });
    if (cacheId) {
        cache.modify({
            id: cacheId,
            fields: {
                isActiveForCompany() {
                    return isActive;
                }
            }
        });
    }
};

/**
 * Hook zur Verwaltung der Dokumentenauswahl für ein Unternehmen
 * Ermöglicht das Aktivieren/Deaktivieren einzelner oder mehrerer Dokumente
 */
export const useDocumentSelection = () => {
    // Mutation für einzelne Dokumentenauswahl
    const [toggleDocument, { loading: toggleLoading }] = useMutation(TOGGLE_DOCUMENT_SELECTION, {
        // Cache direkt aktualisieren statt alle Dokumente neu zu laden
        update(cache, { data: { toggleDocumentSelection } }) {
            updateDocumentInCache(
                cache, 
                toggleDocumentSelection.documentId, 
                toggleDocumentSelection.isActive
            );
        }
    });

    // Mutation für Massenänderungen
    const [bulkToggleDocuments, { loading: bulkToggleLoading }] = useMutation(BULK_TOGGLE_DOCUMENT_SELECTIONS, {
        // Cache für jedes Dokument aktualisieren
        update(cache, { data: { bulkToggleDocumentSelections } }) {
            bulkToggleDocumentSelections.forEach((selection: any) => {
                updateDocumentInCache(
                    cache, 
                    selection.documentId, 
                    selection.isActive
                );
            });
        }
    });

    // Funktion zum Umschalten eines einzelnen Dokuments
    const toggleDocumentSelection = async (documentId: string, isActive: boolean) => {
        try {
            await toggleDocument({
                variables: {
                    documentId,
                    isActive
                }
            });
            return true;
        } catch (error) {
            console.error("Fehler beim Umschalten der Dokumentenauswahl:", error);
            return false;
        }
    };

    // Funktion zum Umschalten mehrerer Dokumente gleichzeitig
    const bulkToggleDocumentSelections = async (documentIds: string[], isActive: boolean) => {
        try {
            await bulkToggleDocuments({
                variables: {
                    documentIds,
                    isActive
                }
            });
            return true;
        } catch (error) {
            console.error("Fehler beim Massenumschalten der Dokumentenauswahl:", error);
            return false;
        }
    };

    return {
        toggleDocumentSelection,
        bulkToggleDocumentSelections,
        loading: toggleLoading || bulkToggleLoading
    };
}; 